import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, BarChart3, Eye } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { BrandLockup } from "@/components/Brand";
import { ExposureReport } from "@/components/ExposureReport";
import { Skeleton } from "@/components/ui/skeleton";

function Body({ data, notFound }) {
  if (notFound)
    return (
      <div data-testid="exposure-not-found" className="pb-card mt-10 flex flex-col items-center gap-3 p-16 text-center">
        <BarChart3 className="h-10 w-10 text-neutral-300" />
        <p className="font-display text-xl font-bold">Report not found</p>
        <p className="text-sm text-neutral-500">This Exposure Check link is invalid or sharing is turned off.</p>
      </div>
    );
  if (!data) return <div className="space-y-6"><Skeleton className="h-36 rounded-2xl" /><Skeleton className="h-72 rounded-2xl" /></div>;
  return <ExposureReport data={data} />;
}

export default function ExposureCheck({ isPublic = false }) {
  const { campaignId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const req = isPublic ? axios.get(`${API_BASE}/public/exposure/${campaignId}`) : api.get(`/campaigns/${campaignId}/exposure`);
    req.then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [campaignId, isPublic]);

  return (
    <div className="min-h-screen">
      {isPublic ? (
        <header className="sticky top-0 z-40 border-b border-[#E7DFCC] bg-[#F7EFDD]/88 backdrop-blur-xl">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6 lg:px-8">
            <BrandLockup />
            <span data-testid="shared-badge" className="flex items-center gap-1.5 rounded-full border border-[#E7DFCC] bg-white px-3 py-1 text-xs font-medium text-[#6B7A80]">
              <Eye className="h-3.5 w-3.5" /> Read-only shared report
            </span>
          </div>
        </header>
      ) : (
        <TopBar />
      )}
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {!isPublic && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Link to={`/campaigns/${campaignId}`} data-testid="exposure-back-btn" className="flex items-center gap-1.5 text-sm text-neutral-500 transition-colors hover:text-black">
              <ArrowLeft className="h-4 w-4" /> Back to campaign
            </Link>
            <span data-testid="exposure-share-path" className="text-xs text-neutral-500">
              Public link (when campaign sharing is on): <span className="font-mono">{window.location.origin}/share/exposure/{campaignId}</span>
            </span>
          </div>
        )}
        <Body data={data} notFound={notFound} />
        {isPublic && <p className="mt-12 text-center text-xs text-[#9aa7ab]">Powered by Playbook Distro · Sound-performance analytics</p>}
      </main>
    </div>
  );
}
